import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { useBranding } from '../../utils/BrandingContext';
import loginBg1 from '../../assets/login-bg-1.png';
import loginBg2 from '../../assets/login-bg-2.png';
import loginBg3 from '../../assets/login-bg-3.png';
import loginBg4 from '../../assets/login-bg-4.png';
import loginBg5 from '../../assets/login-bg-5.png';

const ForgotPassword: React.FC = () => {
  const { branding } = useBranding();

  // Prefer server-provided image URL; fall back to bundled defaults for legacy values.
  const selectedBg = useMemo(() => {
    if (branding.loginBackgroundImage) return branding.loginBackgroundImage;

    const loginBackgrounds: Record<string, string> = {
      '1': loginBg1,
      '2': loginBg2,
      '3': loginBg3,
      '4': loginBg4,
      '5': loginBg5,
    };

    const themeKey = branding.loginBackgroundTheme || '1';
    return loginBackgrounds[themeKey] || loginBg1;
  }, [branding.loginBackgroundImage, branding.loginBackgroundTheme]);

  // Detect branded domain (same logic as Login.tsx)
  const brandedDomain = useMemo(() => {
    const host = window.location.hostname;
    if (!host || host === 'localhost' || host === '127.0.0.1') return '';
    if (host === 'cruvzmail.com' || host.endsWith('.cruvzmail.com')) return '';
    const parts = host.split('.');
    const mailSubdomains = ['mail', 'webmail', 'smtp', 'imap', 'pop', 'mx', 'm', 'email'];
    if (parts.length > 2 && mailSubdomains.includes(parts[0].toLowerCase())) {
      return parts.slice(1).join('.');
    }
    return host;
  }, []);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // null = not submitted yet; string = admin reset success (masked email)
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  // true = regular user request — notification sent to admin
  const [requestSentToAdmin, setRequestSentToAdmin] = useState(false);

  // Custom field styling to match Login.tsx
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      bgcolor: '#ffffff',
      '& fieldset': { borderColor: '#d1d5db', borderWidth: 1 },
      '&:hover fieldset': { borderColor: '#9ca3af' },
      '&.Mui-focused fieldset': { borderColor: '#d4a832', borderWidth: 2 },
    },
    '& .MuiInputBase-input': { fontSize: 14, color: '#111827', paddingTop: '12px', paddingBottom: '12px' },
    '& .MuiInputLabel-root': { 
      color: '#6b7280',
      '&.Mui-focused': { color: '#d4a832' },
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!input.trim()) {
      setError(brandedDomain ? 'Please enter your recovery email address' : 'Please enter your email address');
      return;
    }

    // Basic email format check
    if (!input.includes('@') || !input.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      let payload: Record<string, string>;
      if (brandedDomain) {
        // Branded domain: user enters their recovery email (external email used at signup OTP)
        // Backend looks up the org by that recovery email + loginDomain
        payload = {
          recoveryEmail: input.trim(),
          loginDomain: brandedDomain,
          portalOrigin: window.location.origin,
          email: input.trim(), // DTO requires email, set same value
        };
      } else {
        payload = { email: input.trim() };
      }

      const res = await api.post('/auth/forgot-password', payload);
      // TransformInterceptor wraps data as { success, data: {...}, timestamp }
      const data = res.data?.data || res.data;
      const requestType = data?.requestType;
      const masked = data?.maskedEmail;

      if (requestType === 'user_notification') {
        // Regular user — notification sent to admin, no reset link issued
        setRequestSentToAdmin(true);
      } else if (masked) {
        setMaskedEmail(masked);
      } else {
        setError('No recovery email is configured for this account. Please contact your administrator.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Success screen: regular user — request sent to admin ─────────────────
  if (requestSentToAdmin) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
          backgroundImage: `url(${selectedBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#07111f',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, background: 'linear-gradient(135deg, rgba(4,14,32,0.75) 0%, rgba(4,14,32,0.65) 100%)' }} />
        <Box sx={{ width: '100%', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
          <Box sx={{ maxWidth: 480, width: '100%', bgcolor: '#ffffff', borderRadius: '10px', boxShadow: '0 10px 40px rgba(0,0,0,0.35)', p: { xs: 3, sm: '36px 40px' }, textAlign: 'center' }}>
            <Box sx={{ width: 90, height: 90, borderRadius: '50%', bgcolor: 'rgba(217,119,6,0.1)', border: '2px solid rgba(217,119,6,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <CheckCircleIcon sx={{ color: '#d97706', fontSize: 48 }} />
            </Box>
            <Typography sx={{ fontFamily: '"Inter", "Segoe UI", sans-serif', fontSize: 26, fontWeight: 700, color: '#0f172a', mb: 1.5, letterSpacing: '-0.3px' }}>
              Request Sent
            </Typography>
            <Typography sx={{ fontFamily: '"Inter", "Segoe UI", sans-serif', fontSize: 14, color: '#6b7280', lineHeight: 1.7, mb: 3 }}>
              Your password reset request has been sent to your domain administrator.
            </Typography>
            <Box sx={{ py: 2, px: 3, bgcolor: '#fef3c7', border: '1px solid #fde68a', borderRadius: 2, mb: 3 }}>
              <Typography sx={{ fontSize: 13.5, color: '#92400e', lineHeight: 1.6 }}>
                The admin will reset your password and notify you. Please check your inbox or contact your administrator directly.
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              fullWidth
              size="large"
              sx={{ py: 1.5, fontWeight: 600, fontSize: '0.95rem', borderRadius: '6px', textTransform: 'none', bgcolor: '#d4a832', '&:hover': { bgcolor: '#b88f28' }, boxShadow: '0 2px 8px rgba(212,175,80,0.25)' }}
            >
              Back to Sign In
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Success screen: admin — reset link sent ────────────────────────────────
  if (maskedEmail !== null) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
          backgroundImage: `url(${selectedBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#07111f',
        }}
      >
        {/* Dark overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: 'linear-gradient(135deg, rgba(4,14,32,0.75) 0%, rgba(4,14,32,0.65) 100%)',
          }}
        />

        {/* Centered content */}
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              maxWidth: 480,
              width: '100%',
              bgcolor: '#ffffff',
              borderRadius: '10px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
              p: { xs: 3, sm: '36px 40px' },
              textAlign: 'center',
            }}
          >
            {/* Success icon with golden accent */}
            <Box
              sx={{
                width: 90,
                height: 90,
                borderRadius: '50%',
                bgcolor: 'rgba(212,175,80,0.12)',
                border: '2px solid rgba(212,175,80,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <MarkEmailReadIcon sx={{ color: '#d4a832', fontSize: 48 }} />
            </Box>

            <Typography
              sx={{
                fontFamily: '"Inter", "Segoe UI", sans-serif',
                fontSize: 26,
                fontWeight: 700,
                color: '#0f172a',
                mb: 1.5,
                letterSpacing: '-0.3px',
              }}
            >
              Check Your Email
            </Typography>

            <Typography
              sx={{
                fontFamily: '"Inter", "Segoe UI", sans-serif',
                fontSize: 14,
                color: '#6b7280',
                lineHeight: 1.7,
                mb: 3,
              }}
            >
              We've sent password reset instructions to your recovery address:
            </Typography>

            <Box
              sx={{
                py: 2,
                px: 3,
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                mb: 3,
                display: 'inline-block',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: '#d4a832',
                  fontSize: '1.05rem',
                  letterSpacing: '0.02em',
                }}
              >
                {maskedEmail}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: '"Inter", "Segoe UI", sans-serif',
                fontSize: 13.5,
                color: '#64748b',
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              Open that email and click the <strong style={{ color: '#0f172a' }}>Reset My Password</strong> link. 
              The link expires in <strong style={{ color: '#d4a832' }}>1 hour</strong>.
            </Typography>

            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              fullWidth
              size="large"
              sx={{
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.95rem',
                borderRadius: '6px',
                textTransform: 'none',
                bgcolor: '#d4a832',
                '&:hover': {
                  bgcolor: '#b88f28',
                },
                boxShadow: '0 2px 8px rgba(212,175,80,0.25)',
              }}
            >
              Back to Sign In
            </Button>

            <Typography
              sx={{
                fontFamily: '"Inter", "Segoe UI", sans-serif',
                fontSize: 13,
                color: '#94a3b8',
                mt: 3,
              }}
            >
              Didn't receive it?{' '}
              <Link
                component="button"
                onClick={() => {
                  setMaskedEmail(null);
                  setInput('');
                  setError('');
                }}
                sx={{
                  fontWeight: 600,
                  color: '#d4a832',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Try again
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Form screen ────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
        backgroundImage: `url(${selectedBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#07111f',
      }}
    >
      {/* Dark overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          background: 'linear-gradient(135deg, rgba(4,14,32,0.75) 0%, rgba(4,14,32,0.65) 100%)',
        }}
      />

      {/* Centered content */}
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            bgcolor: '#ffffff',
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
            p: { xs: 3, sm: '32px 36px' },
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 3 }}>
            <img
              src={branding.logo || branding.sidebarIcon || branding.favicon || '/logo.png'}
              alt={branding.companyName || 'CruvzMail'}
              onError={(e) => {
                const t = e.currentTarget;
                t.onerror = null;
                t.style.display = 'none';
              }}
              style={{
                maxWidth: 140,
                height: 70,
                objectFit: 'contain',
                imageRendering: '-webkit-optimize-contrast',
                flexShrink: 0,
              }}
            />
            {branding.companyName && (
              <Typography
                sx={{
                  fontFamily: '"Inter", "Segoe UI", sans-serif',
                  fontSize: 17,
                  fontWeight: 700,
                  color: '#111827',
                  letterSpacing: '-0.2px',
                }}
              >
                {branding.companyName}
              </Typography>
            )}
          </Box>

          {/* Back link */}
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              textDecoration: 'none',
              color: '#6b7280',
              mb: 3,
              fontSize: 13.5,
              fontWeight: 500,
              transition: 'color 0.2s',
              '&:hover': { color: '#d4a832' },
            }}
          >
            <ArrowBackIcon fontSize="small" />
            <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>Back to Sign In</Typography>
          </Link>

          {/* Heading */}
          <Box sx={{ mb: 3.5 }}>
            <Typography
              sx={{
                fontFamily: '"Inter", "Segoe UI", sans-serif',
                fontSize: 24,
                fontWeight: 700,
                color: '#0f172a',
                mb: 1,
                letterSpacing: '-0.3px',
              }}
            >
              Forgot Password?
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", "Segoe UI", sans-serif',
                fontSize: 14,
                color: '#6b7280',
                lineHeight: 1.6,
              }}
            >
              {brandedDomain
                ? `Enter your email address and we'll help you recover access to your account.`
                : "Enter your email address and we'll send reset instructions to your registered recovery email."}
            </Typography>
          </Box>

          {/* Error alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 1.5,
                fontSize: 13.5,
                '& .MuiAlert-icon': { fontSize: 20 },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {brandedDomain ? (
              <TextField
                fullWidth
                label="Your Email Address"
                type="email"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="you@yourdomain.com"
                autoComplete="email"
                helperText="Enter the email address for your account on this domain"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
            ) : (
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="you@yourdomain.com"
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading}
              sx={{
                mt: 3,
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.95rem',
                borderRadius: '6px',
                textTransform: 'none',
                bgcolor: '#d4a832',
                '&:hover': {
                  bgcolor: '#b88f28',
                },
                '&:disabled': {
                  bgcolor: '#e5e7eb',
                },
                boxShadow: '0 2px 8px rgba(212,175,80,0.25)',
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;

